import type {
  StaffQueueRowDto,
  BrandReviewSummaryDto,
  BrandReviewListResponseDto,
  StaffBrandReviewControllerListV1Params,
} from 'src/apis/backend/generated/models';

import {
  StaffQueueControllerQueueV1Sort,
  StaffQueueControllerQueueV1View,
  StaffBrandReviewControllerListV1StateItem,
} from 'src/apis/backend/generated/models';

// ----------------------------------------------------------------------

export type QueueView =
  (typeof StaffQueueControllerQueueV1View)[keyof typeof StaffQueueControllerQueueV1View];

export type QueueSort =
  (typeof StaffQueueControllerQueueV1Sort)[keyof typeof StaffQueueControllerQueueV1Sort];

/**
 * The tabs: which part of the lifecycle is on screen, and nothing else.
 *
 * Active leads and is where the queue opens: it is the lead's working list
 * (internal review ranked first) with production and queued work beneath it as
 * a status read. All open adds client review; Handed off is the tail.
 *
 * Who holds the work, how far along it is and whether it is stuck are filters
 * inside a tab rather than tabs of their own (`QUEUE_SHOWS`). As tabs they were
 * mutually exclusive with the lifecycle, so "my tasks that are active" was a
 * question the screen could not ask.
 *
 * `countKey` names the field on the counts DTO rather than being derived from
 * `value`, because `handed_off` and `handedOff` are spelled differently on the
 * wire and in the response and guessing between them is how a tab silently
 * shows zero.
 */
export const QUEUE_VIEWS: {
  value: QueueView;
  label: string;
  countKey: 'active' | 'all' | 'handedOff';
}[] = [
  { value: StaffQueueControllerQueueV1View.active, label: 'Active', countKey: 'active' },
  { value: StaffQueueControllerQueueV1View.all, label: 'All open', countKey: 'all' },
  {
    value: StaffQueueControllerQueueV1View.handed_off,
    label: 'Handed off',
    countKey: 'handedOff',
  },
];

export type QueueShow = 'mine' | 'unassigned' | 'first_drafts' | 'edits' | 'blocked';

/**
 * The slices that used to be tabs, now a filter over whichever tab is open.
 *
 * The values keep the old view names so a link to `?view=mine` can be read as
 * this filter (see the route). First drafts and edits split by rounds burned
 * rather than status, because a ticket back in production after a round is an
 * edit whatever state it sits in.
 */
export const QUEUE_SHOWS: {
  value: QueueShow;
  label: string;
  match: (row: StaffQueueRowDto, userId: string | null) => boolean;
}[] = [
  {
    value: 'mine',
    label: 'My tasks',
    match: (row, userId) => !!userId && row.assignee?.id === userId,
  },
  { value: 'unassigned', label: 'Unassigned', match: (row) => !row.assignee },
  { value: 'first_drafts', label: 'First drafts', match: (row) => row.roundsUsed === 0 },
  { value: 'edits', label: 'Edits', match: (row) => row.roundsUsed > 0 },
  { value: 'blocked', label: 'Blocked', match: (row) => Boolean(row.blockedReason) },
];

export function isQueueShow(value: unknown): value is QueueShow {
  return QUEUE_SHOWS.some((entry) => entry.value === value);
}

/**
 * The one move a row offers from the list, or null.
 *
 * Pick up on free queued work and Start on queued work the viewer already
 * holds.
 */
export function queueRowAction(
  row: Pick<StaffQueueRowDto, 'status' | 'assignee'>,
  userId: string | null
): 'pick_up' | 'start' | null {
  if (row.status !== 'queued') return null;
  if (!row.assignee) return 'pick_up';
  return !!userId && row.assignee.id === userId ? 'start' : null;
}

/**
 * The rows a person can take in one move: nobody holds them.
 *
 * A held brand's rows are left out: the assign would succeed, and that is the
 * problem. nexmail is producing nothing for the brand
 * until the review clears, so taking the work puts somebody's name on tickets
 * that cannot be published or scheduled, with nothing in the round trip saying
 * why. It is the same judgement the group header makes when it counts held rows
 * out of its unassigned chip, and the two sit beside each other: a header
 * reporting nothing going spare next to a button offering to take it is worse
 * than either alone.
 */
export function takeableRows<Row extends Pick<StaffQueueRowDto, 'assignee' | 'brandHeld'>>(
  rows: Row[]
): Row[] {
  return rows.filter((row) => !row.assignee && !row.brandHeld);
}

/**
 * Who the producer column names: the machine when it holds the ticket, the
 * person holding it otherwise.
 *
 * The agent test is passed in rather than imported, so this file stays free of
 * the screens' shared module and the route that reads the tabs does not load it.
 */
export function queueRowProducer(
  row: Pick<StaffQueueRowDto, 'assignee'>,
  isAgentUser: (user: StaffQueueRowDto['assignee']) => boolean
): 'agent' | 'person' | 'unassigned' {
  if (isAgentUser(row.assignee)) return 'agent';
  if (row.assignee) return 'person';
  return 'unassigned';
}

/**
 * How the rows are ordered, and what each ordering is for.
 *
 * The descriptions matter more than the labels here: "oldest first" and "due
 * first" sound interchangeable until you know one pins overdue work and the
 * other deliberately does not, which is the difference between "what is most
 * urgent" and "what has been sitting here longest".
 */
export const QUEUE_SORTS: { value: QueueSort; label: string }[] = [
  { value: StaffQueueControllerQueueV1Sort.due, label: 'Due first, overdue pinned' },
  { value: StaffQueueControllerQueueV1Sort.created, label: 'Oldest first' },
  { value: StaffQueueControllerQueueV1Sort.send, label: 'Send date' },
  { value: StaffQueueControllerQueueV1Sort.state, label: 'Lifecycle state' },
];

export const DEFAULT_QUEUE_SORT: QueueSort = StaffQueueControllerQueueV1Sort.due;

/**
 * A worklist is scanned, not read: one line per ticket wherever the content
 * allows. Text and chips drop a size inside this table only, because the same
 * chips render at full size on the ticket screen. The Ticket cell opts back into
 * wrapping so a long title grows the row rather than the table.
 *
 * Here rather than beside the ticket table so the Brand reviews tab can read the
 * same way without importing the screen that renders it.
 */
export const DENSE_TABLE_SX = {
  '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: 13, whiteSpace: 'nowrap' },
  '& .MuiTableCell-paddingCheckbox': { px: 0.5 },
  '& .MuiTypography-body2': { fontSize: 13 },
  '& .MuiChip-sizeSmall': { height: 20, fontSize: 11 },
  '& .MuiChip-labelSmall': { px: 0.75 },
  '& .MuiCheckbox-root': { p: 0.5 },
  '& .MuiButton-sizeSmall': { py: 0, minHeight: 26, fontSize: 12 },
};

/**
 * The account beside the brand, unless the two carry the same name.
 *
 * A brand organization is usually itself, so its client row and its account row
 * are named identically and printing both says the name twice. The account is
 * worth the space only where it disambiguates: an agency's brands, and the
 * brands whose customers happened to pick the same word.
 */
export function brandSublabel(brand: string, organizationName: string): string | null {
  const org = organizationName.trim();
  return org && org !== brand.trim() ? org : null;
}

// ----------------------------------------------------------------------

/**
 * The tab after the ticket views, which is not a ticket view.
 *
 * Brand reviews lists what the brand screen said about new brands, so the value
 * exists only in this app's URL. It stays out of `QueueView` on purpose: that
 * type is what the ticket queue api accepts, and a tab the api has never heard
 * of must not be able to reach it.
 */
export const BRAND_REVIEWS_VIEW = 'brand_reviews';

export type QueuePageView = QueueView | typeof BRAND_REVIEWS_VIEW;

export function isQueuePageView(value: unknown): value is QueuePageView {
  return value === BRAND_REVIEWS_VIEW || QUEUE_VIEWS.some((entry) => entry.value === value);
}

/**
 * The tab actually on screen.
 *
 * Brand reviews is a lead's tab, and the route cannot tell who is a lead, so a
 * link to it held by anyone else lands on Active here. Whether someone is a lead
 * is only settled once `/staff/me` answers. Until then the link is held rather
 * than bounced to Active and back, which would also fire an Active request the
 * lead never asked for.
 */
export function resolveQueuePageView(
  view: QueuePageView,
  lead: { isLead: boolean; settled: boolean }
): QueuePageView {
  if (view !== BRAND_REVIEWS_VIEW) return view;
  return lead.isLead || !lead.settled ? view : StaffQueueControllerQueueV1View.active;
}

export type BrandReviewFilter = 'open' | 'rejected' | 'all';

/**
 * The chips over the brand reviews list.
 *
 * Needs a look is where the tab opens: an open review is one the screen flagged
 * for a person and nobody has decided yet. All includes the brands the screen
 * cleared on its own, which is the only way to see how often it lets one through.
 */
export const BRAND_REVIEW_FILTERS: {
  value: BrandReviewFilter;
  label: string;
  /** Absent asks for every state. */
  states?: StaffBrandReviewControllerListV1StateItem[];
}[] = [
  {
    value: 'open',
    label: 'Needs a look',
    states: [StaffBrandReviewControllerListV1StateItem.open],
  },
  {
    value: 'rejected',
    label: 'Rejected',
    states: [StaffBrandReviewControllerListV1StateItem.rejected],
  },
  { value: 'all', label: 'All' },
];

export const DEFAULT_BRAND_REVIEW_FILTER: BrandReviewFilter = 'open';

export function isBrandReviewFilter(value: unknown): value is BrandReviewFilter {
  return BRAND_REVIEW_FILTERS.some((entry) => entry.value === value);
}

/**
 * One page of reviews: the most the api serves. The size is shared with the tab
 * badge so the open list's first page and the badge are the same request, one
 * cache entry that cannot disagree with itself.
 */
export const BRAND_REVIEW_PAGE_SIZE = 100;

export function brandReviewListParams(
  filter: BrandReviewFilter,
  cursor?: string
): StaffBrandReviewControllerListV1Params {
  const states = BRAND_REVIEW_FILTERS.find((entry) => entry.value === filter)?.states;
  return {
    ...(states ? { state: states } : {}),
    limit: BRAND_REVIEW_PAGE_SIZE,
    ...(cursor ? { cursor } : {}),
  };
}

export type QueueScope = { clientId?: string; engagementId?: string };

/**
 * The reviews the queue's brand and engagement filters leave on screen.
 *
 * Filtered here because the reviews api takes neither, and the queue's filters
 * have to mean the same thing on every tab: a lead reading one brand's tickets
 * who switches tabs should see that brand's reviews, not everybody's.
 */
export function reviewsInScope<
  Review extends Pick<BrandReviewSummaryDto, 'clientId' | 'engagementId'>,
>(reviews: Review[], scope: QueueScope): Review[] {
  if (!scope.clientId && !scope.engagementId) return reviews;
  return reviews.filter(
    (review) =>
      (!scope.clientId || review.clientId === scope.clientId) &&
      (!scope.engagementId || review.engagementId === scope.engagementId)
  );
}

/**
 * The count on the Brand reviews tab: open reviews inside the queue's filters,
 * the way the ticket tabs count inside them. It reads one page, so when the api
 * says there is another the count is a floor and says so.
 */
export function brandReviewCount(
  page: Pick<BrandReviewListResponseDto, 'reviews' | 'nextCursor'>,
  scope: QueueScope
): string {
  const count = reviewsInScope(page.reviews, scope).length;
  return page.nextCursor ? `${count}+` : String(count);
}

// ----------------------------------------------------------------------

export type QueueSearch = {
  /** Which tab is on screen. In the URL so a view is shareable. */
  view: QueuePageView;
  /** The filter inside the tab (mine, unassigned, ...). Absent shows everyone. */
  show?: QueueShow;
  /** Narrow to one brand. Set by the brand filter and the "everything for this brand" link. */
  clientId?: string;
  /** Narrow to one engagement. Set by the engagement screens. */
  engagementId?: string;
  /**
   * Row order. In the URL for the same reason the view is: a queue someone sends
   * you should arrive ordered the way they were reading it. Absent means the
   * default, so the common case keeps a clean URL and callers linking to the
   * queue do not have to name an ordering they have no opinion about.
   */
  sort?: QueueSort;
  /** The chip on the Brand reviews tab. Absent means Needs a look, for the same reason as sort. */
  reviews?: BrandReviewFilter;
};

/**
 * The route's search, read from whatever the URL carried.
 *
 * Mine, unassigned, first drafts, edits and blocked were tabs over every open
 * ticket. A link still carrying one lands on All open with that filter, which is
 * the same list it used to show. `needs_lead` falls through to Active, whose
 * first group is that list. Brand reviews is accepted for anyone here; who may
 * actually see it is `resolveQueuePageView`'s call, since only the screen knows.
 */
export function normalizeQueueSearch(search: Record<string, unknown>): QueueSearch {
  const legacyShow = isQueueShow(search.view) ? search.view : undefined;
  return {
    view: isQueuePageView(search.view) ? search.view : legacyShow ? 'all' : 'active',
    show: isQueueShow(search.show) ? search.show : legacyShow,
    clientId: typeof search.clientId === 'string' ? search.clientId : undefined,
    engagementId: typeof search.engagementId === 'string' ? search.engagementId : undefined,
    sort: QUEUE_SORTS.some((entry) => entry.value === search.sort)
      ? (search.sort as QueueSort)
      : undefined,
    reviews:
      isBrandReviewFilter(search.reviews) && search.reviews !== DEFAULT_BRAND_REVIEW_FILTER
        ? search.reviews
        : undefined,
  };
}
